import type * as A from '@/api';
import Workspace from './workspace';
import Connection from './connection';
import DataObject from './dataObject';
import { graphql } from '@/utils/graphql';
import { logger } from '@/utils/logger';
import { assertIsDefined } from '@/utils/typeChecks';


const ns = 'app.services.source';

type SourceConstructorInput = A.GetSourceQuery['getSource'];

export default class Source {
  readonly id: string;

  readonly name: string;

  readonly description?: string;

  readonly connectionId: string;

  readonly workspaceId: string;

  readonly cloudResourceId: string;

  readonly isPaused?: boolean;

  readonly createdAt: Date;

  readonly pausedAt?: Date;

  private constructor(input: SourceConstructorInput) {
    this.id = input.id;
    this.name = input.name;
    this.description = input.description;
    this.connectionId = input.connectionId;
    this.workspaceId = input.workspaceId;
    this.cloudResourceId = input.cloudResourceId;
    this.isPaused = input.connection?.isPaused;
    this.createdAt = new Date(input.connection.createdAt);
    this.pausedAt = input.connection.pausedAt ? new Date(input.connection.pausedAt) : undefined;

    this.getDataObjects = this.getDataObjects.bind(this);
    this.getFivetranConnectionProps = this.getFivetranConnectionProps.bind(this);
    this.getFivetranSyncStatus = this.getFivetranSyncStatus.bind(this);
  }

  async getSharedWithWorkspaces(): Promise<Workspace[]> {
    const sourceId = this.id;
    const data = await graphql('listSourceWorkspaceSharesBySource', { sourceId });
    return await Promise.all(
      data.items.map(async (el) => {
        return await Workspace.getWorkspaceById(el.workspaceId);
      }),
    );
  }

  async getDataObjects(): Promise<DataObject[]> {
    return DataObject.getDataObjectsBySource(this.id);
  }

  async getFivetranConnectionProps(): Promise<{
    id: string;
    isConnectCardCompleted: boolean;
  }> {
    const connections = await Connection.getFivetranConnectionsByWorkspace(this.workspaceId);
    const connection = connections.find((el) => el.id === this.connectionId);
    const fivetranConnection = connection?.fivetranConnection;
    assertIsDefined(fivetranConnection, 'BEEM240219075430');
    return {
      id: fivetranConnection.id,
      isConnectCardCompleted: !!fivetranConnection.isDeployed,
    };
  }

  async getFivetranSyncStatus() {
    const fivetranProps = await this.getFivetranConnectionProps();
    const { id: connectorId } = fivetranProps;

    // Step 1: Check Fivetran sync status
    let step1Complete = false;
    let step1Failed = false;

    try {
      const fivetranResponse = await graphql('fivetranCheckInitialSyncStatus', {
        query: { connectorId },
      });
      const fivetranStatus = JSON.parse(fivetranResponse as string);

      step1Complete = fivetranStatus.completed || false;
      step1Failed = fivetranStatus.completed && !fivetranStatus.successful;
    } catch (error) {
      logger.error({ label: `${ns}.getFivetranSyncStatus.step1.error`, message: error });
      // Step 1 failed - can't proceed, return null
      return null;
    }

    // Step 2: Check BEEM processing status (only if step 1 is complete and successful)
    let step2Complete = false;
    let step2Failed = false;

    if (step1Complete && !step1Failed) {
      try {
        const beemResponse = await graphql('beemAgentCheckSourceSyncStatus', {
          query: { sourceId: this.cloudResourceId },
        });
        step2Complete = beemResponse.step2Complete || false;
        step2Failed = beemResponse.step2Failed || false;
      } catch (error) {
        logger.error({ label: `${ns}.getFivetranSyncStatus.step2.error`, message: error });
        // Step 2 API call failed - mark as failed but preserve Step 1 status
        step2Failed = true;
      }
    }

    return {
      step1Complete,
      step1Failed,
      step2Complete,
      step2Failed,
      lastChecked: Date.now(),
    };
  }

  static async getSourceById(id: string): Promise<Source> {
    logger.info({ label: `${ns}.getSourceById.id`, message: id });
    const data = await graphql('getSource', { id });
    return new Source(data);
  }

  static async getSourcesOwnByWorkspace(workspaceId: string) {
    logger.info({ label: `${ns}.getSourcesOwnByWorkspace.workspaceId`, message: workspaceId });
    const data = await graphql('listSourcesByWorkspace', { workspaceId });
    return data.items.map((el) => new Source(el));
  }

  static async getSourcesSharedWithWorkspace(workspaceId: string) {
    logger.info({ label: `${ns}.getSourcesSharedWithWorkspace.workspaceId`, message: workspaceId });
    const data = await graphql('listSourceWorkspaceSharesByWorkspace', { workspaceId });
    return data.items.map((el) => new Source(el.source));
  }

  static async getSourcesByWorkspace(workspaceId: string) {
    const a = await Source.getSourcesOwnByWorkspace(workspaceId);
    const b = await Source.getSourcesSharedWithWorkspace(workspaceId);
    return a.concat(b);
  }

  static async getSourceIdByFivetranConnectionId(fivetranConnectionId: string): Promise<string> {
    const rs = await graphql('listConnectionsByFivetranConnection', { fivetranConnectionId });
    const connectionId = rs.items[0].id;
    return Source.getSourceIdByConnectionId(connectionId);
  }

  static async getSourceIdByConnectionId(connectionId: string): Promise<string> {
    const rs2 = await graphql('listSourcesByConnection', { connectionId });
    const sourceId = rs2.items[0].id;
    return sourceId;
  }

  // Same as getSourceIdByConnectionId but returns null instead of throwing
  // when no Source row exists. Native OAuth-style connections (e.g. Facebook
  // via the Custom chooser) don't create a Source, so callers that want to
  // operate on the optional Source should use this variant.
  static async findSourceIdByConnectionId(connectionId: string): Promise<string | null> {
    const rs2 = await graphql('listSourcesByConnection', { connectionId });
    const first = rs2.items[0];
    return first ? first.id : null;
  }

  static async checkGlueDatabaseNameUsed(cloudResourceId: string): Promise<boolean> {
    const data = await graphql('listSourcesByCloudResourceId', { cloudResourceId });
    return data.items.length > 0;
  }

  static async update(input: A.UpdateSourceInput): Promise<void> {
    logger.info({ label: 'app.services.connection.update.input', message: input });
    await graphql('updateSource', { input });
  }

  static async deleteIncompleteFivetranSourceAndConnection(id: string): Promise<void> {
    const source = await Source.getSourceById(id);
    const props = await source.getFivetranConnectionProps();
    if (props.isConnectCardCompleted) throw new Error('BEEM240221134815');

    const dataObjects = await source.getDataObjects();
    if (dataObjects.length) throw new Error('BEEM240221134816');

    await graphql('deleteFivetranConnection', { input: { id: props.id } });
    await graphql('deleteConnection', { input: { id: source.connectionId } });
    await graphql('deleteSource', { input: { id } });
  }
}
